import { CSSProperties, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Container, Input, InputGroup, Stack } from 'rsuite'
import { SortType } from 'rsuite/esm/Table'
import { useTranslation } from 'react-i18next'
import { Table } from '@/client/components/Controls'
import { PanelError } from '@/client/components/ErrorProvider'
import { debounce, getTextWidth } from '@/shared'
import { SearchIcon } from '@client/config'
import Loading, { EnumLoadingMode } from '@/client/components/Loading'
import { defaultRowsPerPage } from '@/client/config/constants'
import useViewData, { UseViewDataArgs, UseViewDataResponse, WaitConditionProps } from './useViewData'

const { Column, HeaderCell, Cell } = Table

const loadingStyle: CSSProperties = {
    backgroundColor: 'hsl(0deg 0% 0% / 60%)',
    position: 'absolute',
    left: '-1em',
    bottom: 0,
    margin: '1em',
    padding: '1em',
    width: '100%',
}
const shellStyle: CSSProperties = {
    position: 'relative', // rsuite Table + fillHeight likes this
    flex: '1 1 auto', // take remaining space
    minHeight: 0, // allow the table body to be the scroller
    overflow: 'hidden', // avoid a second scrollbar on the shell
}
const wrapStyle: CSSProperties = {
    position: 'relative', // for the Loading overlay absolute pos
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // CRITICAL: allow child to scroll instead of parent
    overflow: 'hidden', // prevent wrapper scrollbar
}

//
// the dynamic list module
//

interface DynamicListProps<TArgs> {
    tab: any,
    list: any,
    getArgs: (tab: any) => any,
    waitCondition: WaitConditionProps<TArgs>
}

export default function DynamicList<TData extends UseViewDataResponse, TArgs extends UseViewDataArgs>(
    { tab, list, getArgs, waitCondition }: DynamicListProps<TArgs>) {
    const { t } = useTranslation()
    const tableRef = useRef<any>(null)
    const [tableHeight, setTableHeight] = useState(0)
    const [page, setPage] = useState<number>(1)
    const [sortColumn, setSortColumn] = useState<string>()
    const [sortType, setSortType] = useState<SortType>()
    const [searchString, setSearchString] = useState<string>('')
    const [debounceLoading, setDebounceLoading] = useState<boolean>(false)
    const [_reloadData, setReloadData] = useState<number>(0)

    // prevent premature scroll paging after reset
    const [hasInitialData, setHasInitialData] = useState(false)
    const [hasTableHeight, setHasTableHeight] = useState(false)

    // update table height and signal layout ready
    useLayoutEffect(() => {
        if (tableRef.current?.offsetHeight > 0) {
            setTableHeight(tableRef.current.offsetHeight)
            setHasTableHeight(true)
        }
    }, [tableRef.current?.offsetHeight])

    // debounce search input
    const [debouncedString, setDebouncedString] = useState<string>('')
    const debouncedSearch = useMemo(() => {
        return debounce((value: string) => {
            setDebouncedString(value)
            setDebounceLoading(true)
            setHasInitialData(false)
            setPage(1)
            tab.store.data.list = []
            tab.store.data.page = 0
        }, 300)
    }, [])
    useEffect(() => {
        debouncedSearch(searchString)
    }, [searchString])

    // read next page from server
    const args = useMemo(() => {
        return getArgs({
            dataList: tab.store?.data?.id,
            page,
            limit: defaultRowsPerPage,
            sortField: sortColumn,
            sortOrder: sortType,
            q: debouncedString,
        })
    }, [tab.store?.data?.id, page, sortColumn, sortType, debouncedString])
    const { data, loading, error } = useViewData<TData, TArgs>(list, waitCondition, args)
    const pageCount = data?.pageCount ?? 0

    // determine loading state
    const isSearching = (loading || debounceLoading) && page <= 1 && searchString.length > 0
    const isLoading = !isSearching && (loading || (tab.store.data?.page < page && !((data?.rows?.length ?? 0) > 0)))

    // process new data
    useEffect(() => {
        const currentPage = page < pageCount ? page : pageCount // prevent overshooting page count
        if (page !== currentPage) setPage(currentPage)

        //if (data?.rows && tab && (tab.store.data.page !== page || page === 1 || tab.store.data.list.length === 0)) {
        if (data?.rows && tab.store.data.page !== currentPage) {
            const isFirstPage = currentPage === 1
            const updatedList = isFirstPage ? [...data.rows] : [...(tab.store?.data?.list || []), ...data.rows]
            tab.store.data.list = updatedList
            tab.store.data.page = currentPage
            tab.store.data.header = data.header

            // trigger re-render with new data
            setReloadData(prev => prev + 1)

            if (isFirstPage && data.rows.length > 0) {
                setHasInitialData(true)
            }
        }

        setDebounceLoading(false)
    }, [data])

    const allowScrolling = () => {
        return !(
            isLoading ||
            isSearching ||
            !hasInitialData ||
            !hasTableHeight ||
            tab.store?.data?.list?.length === 0 ||
            tab.store?.data?.page !== page ||
            pageCount <= page
        )
    }

    // handle virtual scroll
    const handleScroll = (x: number, y: number) => {
        if (!allowScrolling()) return

        const contextHeight = (tab.store.data?.list?.length || 0) * 46
        const top = Math.abs(y)
        console.log('scroll', {
            page,
            pageCount,
            height: contextHeight - tableHeight,
            calced: contextHeight - tableHeight - top,
            top,
            tableHeight,
            //list: tab.store.data?.list?.length,
            //response: data,
        })

        if (contextHeight - tableHeight - top < 300) {
            //console.warn('fetching next page', page + 1)
            setPage(page + 1)
        }
    }

    const renderEmpty = () => {
        if (isLoading || isSearching) return null
        return <div className="rs-table-body-info">{t('No data found')}</div>
    }

    if (error) {
        return <PanelError message={error?.message ?? t('Unknown error')} />
    }

    const loaderContainerStyle: CSSProperties = {
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'var(--rs-bg-card)',
        padding: 20,
        zIndex: 1
    };

    function renderLoading(loading: ReactNode): ReactNode {
        return <Loading mode={EnumLoadingMode.Inline} />
        // return (
        //     <div style={loaderContainerStyle}>
        //         <Placeholder.Grid rows={15} columns={8} active />
        //     </div>
        // );
    }

    return (
        <div style={wrapStyle}>
            <div className="search-container">
                <InputGroup className="search-bar">
                    <Input placeholder={t('Search')} onChange={setSearchString} />
                    <InputGroup.Addon>
                        <SearchIcon />
                    </InputGroup.Addon>
                </InputGroup>
                <Stack>
                    {data?.filterTotal !== data?.total && <span>{t('{{filtered}} /', { filtered: data.filterTotal })}</span>}
                    {data?.total > 0 && <span>{t('{{count}} {{rows}}', { count: data.total, rows: data?.total === 1 ? t('row') : t('rows') })}</span>}
                </Stack>
            </div>
            <div ref={tableRef} className="table-wrapper" style={shellStyle}>
                <Table
                    data={tab.store.data.list}
                    loading={isSearching}
                    onScroll={handleScroll}
                    shouldUpdateScroll={false}
                    renderEmpty={renderEmpty}
                    renderLoading={renderLoading}
                    virtualized
                    fillHeight
                >
                    <Column key={'index'}>
                        <HeaderCell>{t('Line')}</HeaderCell>
                        <Cell>{(rowData, rowIndex) => (rowIndex || 0) + 1}</Cell>
                    </Column>

                    {tab.store?.data?.header?.map((item: any) => (
                        <Column key={item.column} width={getTextWidth(item)} resizable>
                            <HeaderCell>{item.field}</HeaderCell>
                            <Cell dataKey={item.column} fullText />
                        </Column>
                    ))}
                </Table>

                {isLoading && <Container style={loadingStyle}>{t('Loading...')}</Container>}
            </div>
        </div >
    )
}